import { Component, Input, Output, EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { TopicSelectorDialogComponent } from './topicSelectorDialog.component';
import { first, filter } from 'rxjs/operators';
import * as _ from 'lodash';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

@Component({
    selector: 'briefing-topics-editor',
    styles: [`
        .topicsPanel {
            overflow-y: auto;
            height: calc(100vh - 225px);
        }
        .cdk-column-published.mat-column-published,
        .cdk-column-followersCount.mat-column-followersCount,
        .cdk-column-itemsCount.mat-column-itemsCount,
        .cdk-column-actions.mat-column-actions {
            text-align: center;
        }
    `],
    template: `
        <div fxLayout="column">
            <div fxFlex="100">
                <h5 *ngIf="type === 'SLACK'">The most recent {{slackMaxItems}} items for each selected topic will be included in each briefing.</h5>
                <span fxFlex></span>
                <button mat-button color="primary" (click)="addTopics()">Add Topics</button>
            </div>
            <table mat-table 
                   [dataSource]="topic$"
                   cdkDropList
                   [cdkDropListData]="topic$"
                   (cdkDropListDropped)="drop($event)" 
                   fxFlex="100" 
                   style="width: 100%;">
                <ng-container matColumnDef="title">
                    <th mat-header-cell *matHeaderCellDef>Title</th>
                    <td mat-cell *matCellDef="let row">
                        <a *ngIf="row.type === 'COLLECTION'" [routerLink]="['/topics', row.id]">{{row.title}}</a>
                        <a *ngIf="row.type !== 'COLLECTION'" [routerLink]="['/settings/sources', row.id]">{{row.title}}</a>
                    </td>
                </ng-container>
                <ng-container matColumnDef="published">
                    <th mat-header-cell *matHeaderCellDef width="75px">Published</th>
                    <td mat-cell *matCellDef="let row"><mat-icon *ngIf="row.published">check_circle_outline</mat-icon></td>
                </ng-container>
                <ng-container matColumnDef="followersCount">
                    <th mat-header-cell *matHeaderCellDef width="75px">Followers</th>
                    <td mat-cell *matCellDef="let row">{{row.followersCount}}</td>
                </ng-container>
                <ng-container matColumnDef="itemsCount">
                    <th mat-header-cell *matHeaderCellDef width="75px">Items</th>
                    <td mat-cell *matCellDef="let row">{{ row.itemsCount }}</td>
                </ng-container>
                <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef></th>
                    <td mat-cell *matCellDef="let row">
                        <button mat-icon-button (click)="removeTopic(row)" style="float: right;"><mat-icon>clear</mat-icon></button>
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="columns"></tr>
                <tr mat-row cdkDrag *matRowDef="let row; columns: columns;"></tr>
            </table>
            <div *ngIf="topic$ | async as results">
                <div *ngIf="!results.length">
                    <h5 style="text-align: center">
                        <p>No topics have been selected yet for this Briefing.</p>
                        <p>Choose "Add Topics" to select desired topics.</p>
                    </h5>
                </div>
            </div>
        </div>
    `
})
export class BriefingTopicsEditorComponent {
    private baseColumns = ['title', 'published', 'followersCount', 'itemsCount'];
    columns = this.baseColumns.concat(['actions']);
    @Input() topic$: BehaviorSubject<any>;
    @Input() type: string;
    @Output() saveTopics = new EventEmitter<void>();
    @Output() reorderTopics = new EventEmitter<{ currentIndex: number, previousIndex: number}>();
    slackMaxItems = 3;

    constructor(private dialog: MatDialog) {}

    removeTopic(row):void {
        const streams: any[] = this.topic$.value;
        // Do not assume that the api will return the streams in the same 
        // order as the array from the briefing 
        const streamIndex = streams.findIndex(stream => stream.id === row.id);
        streams.splice(streamIndex, 1); 
        this.topic$.next(streams);
        this.saveTopics.emit();
    }

    drop($event: CdkDragDrop<any>): void {
        this.reorderTopics.emit({ previousIndex: $event.previousIndex, currentIndex: $event.currentIndex });
    }

    addTopics() {
        this.dialog.open(TopicSelectorDialogComponent, { data: {currentTopics: this.topic$.value.map(topic => topic.id)}}).afterClosed().pipe(first(), filter(res => !!res))
        .subscribe((topics: any[]) => {
            this.topic$.next(_.uniqBy(topics.concat(this.topic$.value), topic => topic.id));
            this.saveTopics.emit();
        });
    }
}
